/**
 * Public PM-facing status page. Rendered at /status/:slug. No auth, no
 * AppShell wrapper. Opaque ULID slug.
 *
 * Wireframe: docs/wireframes/10-pm-status-page.html
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Calendar, Clock, Loader2, MapPin, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import type {
  PublicStatusResponse,
  ProjectStatus,
  ScheduleDay,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { SiteMap } from '@/components/ui/SiteMap';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime, formatDate } from '@/lib/format';

export function PublicStatus() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // Signed-in viewers (crew/supervisor/PM following their own link) get a way
  // back into the app; anonymous requester links don't. This page is reachable
  // without auth, so isAuthenticated may simply be false.
  const { isAuthenticated } = useAuth();
  // Scoped advance token from the on-site notification link (ADR-0015).
  const [searchParams] = useSearchParams();
  const advanceToken = searchParams.get('t');
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<PublicStatusResponse>({
    queryKey: ['public-status', slug],
    enabled: !!slug,
    refetchInterval: 30_000,
    queryFn: () => apiCall<PublicStatusResponse>(`/public/status/${slug}`),
  });

  /**
   * The PM advances on_site → punch by tapping "Site is ready". Calls the
   * public, no-auth endpoint and refetches.
   */
  const advance = useMutation<{ status: ProjectStatus }, Error, void>({
    mutationFn: () =>
      apiCall<{ status: ProjectStatus }>(
        `/public/status/${slug}/advance${advanceToken ? `?token=${encodeURIComponent(advanceToken)}` : ''}`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast.success('Marked ready for final inspection · the Vari crew is notified');
      qc.invalidateQueries({ queryKey: ['public-status', slug] });
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  });

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-6 h-6 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft p-6">
        <div className="max-w-md text-center space-y-3 text-ink">
          <h1 className="text-xl font-semibold">Status page not found</h1>
          <p className="text-sm text-ink-dim">
            The link you followed may have been revoked or mistyped. Contact your Vari
            project manager for an updated link.
          </p>
        </div>
      </div>
    );
  }

  const scheduledLabel =
    scheduleRangeLabel(data.schedule) ??
    (data.stage_timestamps.scheduled ? formatDate(data.stage_timestamps.scheduled) : null);

  return (
    <div className="min-h-full bg-fill-soft">
      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {/* Brand bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="font-bold text-lg tracking-tight">VARI · FMS</div>
          {isAuthenticated ? (
            // A signed-in viewer can jump straight to the project workspace; the
            // requester (no session) just sees the status label.
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/projects/${data.project_id}`)}
                className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
              >
                Open project in app <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-[11px] text-ink-faint hover:text-ink uppercase tracking-wide"
              >
                Back to app
              </button>
            </div>
          ) : (
            <div className="text-[11px] text-ink-faint uppercase tracking-wide">Project status</div>
          )}
        </div>

        {/* Header card */}
        <div className="bg-bg rounded-xl border border-line p-6 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold leading-tight">{data.project_title}</h1>
              <p className="text-sm text-ink-dim mt-0.5">{data.client_name}</p>
            </div>
            {data.priority !== 'normal' && (
              <span className="text-[11px] font-semibold uppercase tracking-wide text-status-blocked-fg bg-status-blocked px-2 py-1 rounded-full">
                {data.priority}
              </span>
            )}
          </div>

          {data.status === 'blocked' && (
            <div className="flex items-start gap-2 text-sm text-status-blocked-fg bg-status-blocked rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                This project is temporarily on hold — your Vari project manager will reach out
                shortly.
              </span>
            </div>
          )}

          {/* Hierarchical stage timeline: main stages with substages + when each
              stage was reached. */}
          <StageTimeline
            status={data.status}
            stageTimestamps={data.stage_timestamps}
            scheduledLabel={scheduledLabel}
          />
        </div>

        {/* Schedule card — the committed working windows, one row per day. */}
        {data.schedule.length > 0 && (
          <div className="bg-bg rounded-xl border border-line p-6 shadow-sm">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ink-faint mb-3">
              <Calendar className="w-3.5 h-3.5" />
              Schedule
            </div>
            <ul className="space-y-2.5">
              {data.schedule.map((d, i) => (
                <li key={d.date} className="flex items-center gap-3">
                  <div className="grid place-items-center w-10 h-10 rounded-lg bg-fill-soft flex-shrink-0">
                    <span className="text-[9px] uppercase tracking-wide text-ink-faint leading-none">
                      {format(parseISO(d.date), 'MMM')}
                    </span>
                    <span className="text-base font-semibold leading-none mt-0.5">
                      {format(parseISO(d.date), 'd')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {data.schedule.length > 1 && (
                        <span className="text-ink-faint mr-1.5">Day {i + 1}</span>
                      )}
                      {formatDayLabel(d.date)}
                    </div>
                    <div className="text-xs text-ink-dim flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatTime12(d.start_time)} – {formatTime12(d.end_time)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Site card */}
        {data.site_address && (
          <div className="bg-bg rounded-xl border border-line p-6 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-ink-faint mb-2">
              Site
            </div>
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 mt-0.5 text-ink-faint" />
              <div>
                {data.site_address.line1}
                {data.site_address.line2 && (
                  <>
                    <br />
                    {data.site_address.line2}
                  </>
                )}
                <br />
                {data.site_address.city}, {data.site_address.state}{' '}
                {data.site_address.postal_code}
                {data.site_contact_name && (
                  <div className="text-ink-dim mt-2 flex items-center gap-1">
                    <User className="w-3.5 h-3.5" /> {data.site_contact_name}
                  </div>
                )}
              </div>
            </div>
            <SiteMap
              latitude={data.site_address.latitude}
              longitude={data.site_address.longitude}
              label={`${data.project_title} · site`}
              expandable
              className="h-44 mt-4"
            />
          </div>
        )}

        {/* Crew & tasks card */}
        {data.tasks.length > 0 && (
          <div className="bg-bg rounded-xl border border-line p-6 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-ink-faint mb-3">
              Crew &amp; tasks
            </div>
            <ul className="space-y-3">
              {data.tasks.map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 pb-3 last:pb-0 border-b border-line last:border-0"
                >
                  <span
                    className={cn(
                      'mt-1 w-2 h-2 rounded-full flex-shrink-0',
                      t.status === 'complete'
                        ? 'bg-status-complete-fg'
                        : t.status === 'in_progress' || t.status === 'on_site'
                          ? 'bg-status-onsite-fg'
                          : t.status === 'en_route'
                            ? 'bg-status-enroute-fg'
                            : 'bg-line-strong',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{t.title}</div>
                    <div className="text-xs text-ink-faint">
                      {t.scheduled_start && formatDateTime(t.scheduled_start)}
                      {t.scheduled_end && ` – ${formatDateTime(t.scheduled_end).split('· ')[1]}`}
                    </div>
                    {t.crew.length > 0 && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                        {t.crew.map((c, ci) => (
                          <div key={ci} className="flex items-center gap-1.5">
                            <Avatar name={c.display_name} src={c.avatar_url} className="w-6 h-6" />
                            <span className="text-xs text-ink-dim">{c.display_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* PM advance ("Site is ready") — requires the scoped token from the
            on-site notification link (ADR-0015); a bare slug is view-only. */}
        {data.pm_can_advance && advanceToken && (
          <div className="bg-status-onsite rounded-xl p-6 text-status-onsite-fg">
            <div className="font-semibold mb-1">Site is ready for final inspection?</div>
            <p className="text-sm opacity-90 mb-3">
              The Vari crew is on-site. When you've inspected the install, advance the
              project to the final-inspection stage — Leo's team gets the notification
              automatically.
            </p>
            <Button
              variant="outline"
              onClick={() => setConfirmAdvance(true)}
              disabled={advance.isPending}
            >
              {advance.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Site is ready →
            </Button>
            <ConfirmDialog
              open={confirmAdvance}
              onClose={() => setConfirmAdvance(false)}
              onConfirm={() => {
                setConfirmAdvance(false);
                advance.mutate();
              }}
              title="Site is ready?"
              body="This advances the project to the final-inspection stage and notifies the Vari crew. You can't undo it from this page."
              confirmLabel="Yes, site is ready"
              busy={advance.isPending}
            />
          </div>
        )}

        {data.pm_can_advance && !advanceToken && (
          <div className="bg-bg rounded-xl border border-line p-6 text-sm text-ink-dim">
            The crew is on-site. To confirm the site is ready for final inspection, open
            the link from your "crew on site" notification — this view-only link
            can't advance the project.
          </div>
        )}

        {/* Recap */}
        {data.status === 'complete' && (data.recap_url || data.recap_pdf_url) && (
          <div className="bg-bg rounded-xl border border-line p-6 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-ink-faint mb-3">
              Project recap
            </div>
            {data.recap_url && (
              <a
                href={data.recap_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-accent hover:underline mr-4"
              >
                View recap →
              </a>
            )}
            {data.recap_pdf_url && (
              <a
                href={data.recap_pdf_url}
                className="text-sm font-medium text-accent hover:underline"
              >
                Download PDF →
              </a>
            )}
          </div>
        )}

        <footer className="text-[11px] text-ink-faint text-center pt-3">
          Questions? Reply to the email this link came in, or message your Vari project
          manager. This page auto-refreshes every 30 seconds.
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage timeline
// ---------------------------------------------------------------------------

type StageKey =
  | 'scheduled'
  | 'en_route'
  | 'on_site'
  | 'punch'
  | 'pending_closeout'
  | 'complete';

/** Linear order of the leaf stages — drives done/active/upcoming. */
const STAGE_ORDER: StageKey[] = [
  'scheduled',
  // en_route retired from the customer timeline (demo #7) — work reads as
  // "Installing" once a crew starts; a legacy en_route status maps to on_site.
  'on_site',
  'punch',
  'pending_closeout',
  'complete',
];

type SubStage = { key: StageKey; label: string; hint: string };
type MainStage = { label: string; subs: SubStage[] };

/** Four customer-legible milestones; the busy middle two carry substages. */
const MAIN_STAGES: MainStage[] = [
  {
    label: 'Scheduled',
    subs: [{ key: 'scheduled', label: 'Scheduled', hint: 'Install booked and crew assigned' }],
  },
  {
    label: 'On site',
    subs: [{ key: 'on_site', label: 'Installing', hint: 'Work underway on site' }],
  },
  {
    label: 'Wrapping up',
    subs: [
      { key: 'punch', label: 'Final inspection', hint: 'Final inspection with you' },
      { key: 'pending_closeout', label: 'Final review', hint: 'Vari finalizing the job' },
    ],
  },
  {
    label: 'Complete',
    subs: [{ key: 'complete', label: 'Complete', hint: 'All done — recap available below' }],
  },
];

type NodeState = 'done' | 'active' | 'upcoming';

function StageTimeline({
  status,
  stageTimestamps,
  scheduledLabel,
}: {
  status: ProjectStatus;
  stageTimestamps: PublicStatusResponse['stage_timestamps'];
  scheduledLabel: string | null;
}) {
  // When blocked, the project has no "current" stage; we still light up every
  // stage it actually reached (those carry an event timestamp) so the customer
  // sees how far it got before the hold.
  const isBlocked = status === 'blocked';
  // A legacy/override en_route status maps to on_site ("Installing") — the en
  // route stage was retired from the customer timeline (demo #7).
  const effectiveStatus = status === 'en_route' ? 'on_site' : status;
  const currentIdx = isBlocked ? -1 : STAGE_ORDER.indexOf(effectiveStatus as StageKey);

  const nodeState = (key: StageKey): NodeState => {
    if (isBlocked) return stageTimestamps[key] ? 'done' : 'upcoming';
    const idx = STAGE_ORDER.indexOf(key);
    if (idx < currentIdx) return 'done';
    // 'complete' is terminal — when reached it's finished, not "in progress",
    // so the whole bar reads done (green) rather than an active blue tip.
    if (idx === currentIdx) return key === 'complete' ? 'done' : 'active';
    return 'upcoming';
  };

  const mainState = (subs: SubStage[]): NodeState => {
    const states = subs.map((s) => nodeState(s.key));
    if (states.some((s) => s === 'active')) return 'active';
    if (states.every((s) => s === 'done')) return 'done';
    if (states.some((s) => s === 'done')) return 'active';
    return 'upcoming';
  };

  return (
    <div className="grid grid-cols-4 gap-x-2">
      {MAIN_STAGES.map((main) => {
        const ms = mainState(main.subs);
        const isLeaf = main.subs.length === 1;
        // Leaf milestones (Scheduled / Complete) read better with a date than a
        // substage list: Scheduled → the booked work date(s), Complete → when.
        const leafDetail =
          main.label === 'Scheduled'
            ? scheduledLabel
            : main.label === 'Complete' && stageTimestamps.complete
              ? formatDate(stageTimestamps.complete)
              : null;
        return (
          <div key={main.label} className="space-y-1.5">
            {/* segment bar — one sub-segment per substage (nested progress) */}
            <div className="flex gap-0.5">
              {main.subs.map((sub) => {
                const ss = nodeState(sub.key);
                return (
                  <div
                    key={sub.key}
                    className={cn(
                      'h-1.5 rounded-full flex-1',
                      ss === 'done'
                        ? 'bg-status-complete-fg'
                        : ss === 'active'
                          ? 'bg-status-onsite-fg'
                          : 'bg-line',
                    )}
                  />
                );
              })}
            </div>

            {/* main-stage label */}
            <div
              className={cn(
                'text-[11px] font-semibold uppercase tracking-wide leading-tight',
                ms === 'active'
                  ? 'text-status-onsite-fg'
                  : ms === 'upcoming'
                    ? 'text-ink-faint'
                    : 'text-ink',
              )}
            >
              {main.label}
            </div>

            {/* leaf detail line (date) */}
            {isLeaf && leafDetail && (
              <div className="text-[10px] text-ink-faint leading-tight">{leafDetail}</div>
            )}

            {/* substages — smaller, listed within the column */}
            {!isLeaf && (
              <ul className="space-y-1 pt-0.5">
                {main.subs.map((sub) => {
                  const ss = nodeState(sub.key);
                  const ts = stageTimestamps[sub.key];
                  return (
                    <li key={sub.key} className="flex items-start gap-1 leading-tight">
                      <span
                        className={cn(
                          'mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0',
                          ss === 'done'
                            ? 'bg-status-complete-fg'
                            : ss === 'active'
                              ? 'bg-status-onsite-fg animate-pulse'
                              : 'bg-line-strong',
                        )}
                      />
                      <div className="min-w-0">
                        <div
                          className={cn(
                            'text-[11px] leading-tight',
                            ss === 'active'
                              ? 'text-status-onsite-fg font-medium'
                              : ss === 'done'
                                ? 'text-ink'
                                : 'text-ink-faint',
                          )}
                        >
                          {sub.label}
                        </div>
                        {ts && (ss === 'done' || ss === 'active') && (
                          <div className="text-[10px] text-ink-faint leading-tight">
                            {formatTimeShort(ts)}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule formatting
// ---------------------------------------------------------------------------

/** "08:00" → "8:00 AM". The schedule stores 24h HH:MM strings. */
function formatTime12(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mStr ?? '00'} ${period}`;
}

/** ISO datetime → "9:12 AM". Compact time for the substage labels. */
function formatTimeShort(iso: string): string {
  try {
    return format(parseISO(iso), 'h:mm a');
  } catch {
    return '';
  }
}

/** "2026-06-16" → "Tue, Jun 16" (local; date-only, no TZ shift). */
function formatDayLabel(date: string): string {
  try {
    return format(parseISO(date), 'EEE, MMM d');
  } catch {
    return date;
  }
}

/** Compact range across a multi-day schedule: "Jun 16", "Jun 16 – 18",
 *  or "Jun 28 – Jul 1". Null when there are no days. */
function scheduleRangeLabel(schedule: ScheduleDay[]): string | null {
  if (schedule.length === 0) return null;
  try {
    const firstDay = schedule[0]!;
    const lastDay = schedule[schedule.length - 1]!;
    const first = parseISO(firstDay.date);
    const last = parseISO(lastDay.date);
    if (firstDay.date === lastDay.date) {
      return format(first, 'MMM d');
    }
    if (format(first, 'MMM') === format(last, 'MMM')) {
      return `${format(first, 'MMM d')} – ${format(last, 'd')}`;
    }
    return `${format(first, 'MMM d')} – ${format(last, 'MMM d')}`;
  } catch {
    return null;
  }
}
